import React, { useMemo } from "react";
import { Layers, Loader2, Receipt, Sparkles, Ticket } from "lucide-react";
import { TeamLogo } from "../bondhouse/FixtureStatus";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  formatOdds,
  formatPlacedAt,
  formatSelectionLabel,
  resolveLegFixtureStatus,
  resolveLegScore,
  ticketStatusMeta,
  ticketTapeSummary,
} from "../../utils/bettorsDeskHelpers";

const TapeSkeleton = () => (
  <div className="bd-tape-skeleton" aria-hidden>
    {[0, 1, 2].map((i) => (
      <div key={i} className="bd-tape-skeleton-row">
        <span className="bd-tape-skeleton-bar" />
        <div>
          <span className="bd-tape-skeleton-line bd-tape-skeleton-line-sm" />
          <span className="bd-tape-skeleton-line" />
          <span className="bd-tape-skeleton-metrics">
            <span />
            <span />
            <span />
          </span>
        </div>
      </div>
    ))}
  </div>
);

const FixtureStrip = ({ leg, showLegStatus }) => {
  const legMeta = ticketStatusMeta(leg.status);
  const fxMeta = resolveLegFixtureStatus(leg);
  const score = resolveLegScore(leg);
  const hasScore = !!score;
  const homeName = leg.home?.name || leg.matchLabel?.split(" vs ")?.[0] || "Home";
  const awayName =
    leg.away?.name || leg.matchLabel?.split(" vs ")?.slice(1).join(" vs ") || "Away";
  const pickLabel = formatSelectionLabel(leg.selectionLabel);

  return (
    <div className="bd-tape-fixture-strip">
      <div className="bd-tape-fixture-board">
        <div className="bd-tape-fixture-side bd-tape-fixture-side-home">
          <TeamLogo team={{ ...leg.home, name: homeName }} size="sm" />
          <span className="bd-tape-fixture-name" title={homeName}>
            {homeName}
          </span>
        </div>

        <div className={`bd-tape-fixture-center bd-tape-fixture-center-${fxMeta.tone}`}>
          {hasScore ? (
            <span className="bd-tape-fixture-scoreline tabular-nums">
              {score.phase ? (
                <span className="bd-tape-fixture-score-phase">{score.phase}</span>
              ) : null}
              {score.home}
              <span className="bd-tape-fixture-score-sep">–</span>
              {score.away}
            </span>
          ) : null}
          <span className="bd-tape-fixture-status-chip">
            <span className="bd-tape-fixture-status-code">{fxMeta.code || "—"}</span>
            <em>{fxMeta.label}</em>
          </span>
        </div>

        <div className="bd-tape-fixture-side bd-tape-fixture-side-away">
          <span className="bd-tape-fixture-name" title={awayName}>
            {awayName}
          </span>
          <TeamLogo team={{ ...leg.away, name: awayName }} size="sm" />
        </div>
      </div>

      {(pickLabel || leg.oddValue != null || showLegStatus) && (
        <div className="bd-tape-pick-bar">
          <div className="bd-tape-pick-copy">
            {pickLabel ? <span className="bd-tape-pick-label">{pickLabel}</span> : null}
            {leg.oddValue != null && (
              <em className="bd-tape-pick-odd tabular-nums">
                {Number(leg.oddValue).toFixed(2)}×
              </em>
            )}
          </div>
          {showLegStatus && (
            <span className={`bd-ticket-status bd-ticket-status-${legMeta.tone}`}>
              <span className="bd-ticket-status-dot" />
              {legMeta.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const TicketTapeRow = ({ ticket, isLast }) => {
  const meta = ticketStatusMeta(ticket.status);
  const legs = Array.isArray(ticket.legs) ? ticket.legs : [];
  const openLegs = Number(ticket.openLegs) || 0;
  const legCount = Number(ticket.legCount) || legs.length || 0;
  const isMulti = legCount > 1;
  const isSingle = legs.length === 1;
  const accent = meta.color;

  return (
    <li className="bd-tape-row" style={{ "--tape-accent": accent }}>
      <div className="bd-tape-spine" aria-hidden>
        <span className="bd-tape-dot" />
        {!isLast && <span className="bd-tape-line" />}
      </div>

      <article className={`bd-tape-card ${isSingle ? "bd-tape-card-single" : ""}`}>
        <header className="bd-tape-card-head">
          <span className={`bd-ticket-status bd-ticket-status-${meta.tone}`}>
            <span className="bd-ticket-status-dot" />
            {meta.label}
          </span>
          <time className="bd-tape-when" dateTime={ticket.placedAt || undefined}>
            {formatPlacedAt(ticket.placedAt)}
          </time>
        </header>

        {legs.length > 0 ? (
          <div className={`bd-tape-legs ${isMulti ? "bd-tape-legs-multi" : "bd-tape-legs-single"}`}>
            {legs.map((leg) => (
              <FixtureStrip
                key={leg.fixtureId || leg.legIndex}
                leg={leg}
                showLegStatus={isMulti}
              />
            ))}
          </div>
        ) : legCount > 0 ? (
          <div className="bd-tape-fixture bd-tape-fixture-muted">
            <span className="bd-tape-fixture-label">
              Fixture details unavailable — refresh to reload legs.
            </span>
          </div>
        ) : null}

        <div className="bd-tape-metrics">
          <div className="bd-tape-metric">
            <em>Stake</em>
            <strong className="tabular-nums">{formatCash(ticket.stake, { compact: true })}</strong>
          </div>
          <div className="bd-tape-metric">
            <em>Max pay</em>
            <strong className="tabular-nums">
              {formatCash(ticket.potentialPayout, { compact: true })}
            </strong>
          </div>
          <div className="bd-tape-metric bd-tape-metric-odds">
            <em>Odds</em>
            <strong className="tabular-nums">{formatOdds(ticket.totalOdds)}</strong>
          </div>
        </div>

        {isMulti && (
          <footer className="bd-tape-foot">
            <span className="bd-tape-chip">
              <Layers size={8} />
              {legCount} legs
              {openLegs > 0 && (
                <em className="bd-tape-open-legs">{openLegs} open</em>
              )}
            </span>
            <span className="bd-tape-chip bd-tape-chip-accent">
              <Sparkles size={8} />
              Acca
            </span>
          </footer>
        )}
      </article>
    </li>
  );
};

const TicketTape = ({ tickets, loading, error }) => {
  const summary = useMemo(() => ticketTapeSummary(tickets), [tickets]);

  return (
    <section className="bd-tape-section" aria-labelledby="bd-tape-title">
      <header className="bd-tape-head">
        <div className="bd-tape-head-main">
          <Ticket size={11} />
          <h3 id="bd-tape-title">Ticket tape</h3>
          {!loading && tickets.length > 0 && (
            <span className="bd-tape-count">{summary.count}</span>
          )}
        </div>
        {!loading && tickets.length > 0 && (
          <div className="bd-tape-summary">
            <span>
              <em>Open</em>
              <strong>{summary.open}</strong>
            </span>
            <span>
              <em>Stake</em>
              <strong className="tabular-nums">{formatCash(summary.stake, { compact: true })}</strong>
            </span>
            {summary.open > 0 && (
              <span>
                <em>Exposure</em>
                <strong className="tabular-nums">{formatCash(summary.exposure, { compact: true })}</strong>
              </span>
            )}
          </div>
        )}
      </header>

      <div className="bd-tape-body">
        {loading && (
          <div className="bd-tape-loading" role="status">
            <Loader2 size={14} className="animate-spin" />
            <span>Loading ticket tape…</span>
            <TapeSkeleton />
          </div>
        )}

        {error && !loading && (
          <div className="bd-tape-error-wrap">
            <p className="bd-tape-error">{error}</p>
          </div>
        )}

        {!loading && !error && tickets.length === 0 && (
          <div className="bd-tape-empty">
            <Receipt size={16} strokeWidth={1.5} />
            <strong>No tickets yet</strong>
            <p>This bettor hasn&apos;t placed any tickets on this house.</p>
          </div>
        )}

        {!loading && tickets.length > 0 && (
          <ol className="bd-tape-list">
            {tickets.map((ticket, index) => (
              <TicketTapeRow
                key={ticket.ticketId}
                ticket={ticket}
                isLast={index === tickets.length - 1}
              />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
};

export default TicketTape;
